import Breadcrumb from 'atoms/breadcrumb';
import Button from 'atoms/button';
import PropTypes from 'prop-types';
import React from 'react';
import Title from 'atoms/title';
import Tooltip from 'atoms/tooltip';


export default function PageEstablishmentInfo({
  allPartners, area, arrival, breadcrumb, categoryLiteral, city, className,
  clickoutSubsections, combinedRating, handleScroll, hasCancellationPolicy,
  instantBook, leaving, maxDayPrice, maxGuests, minDayPrice, numberFormat, numberOfComments, numberOfNights,
  partnersWithoutAvailabilityClickout, recommendation, rooms, searchGuests,
  stars, t, title, titlePreposition,
}) {
  const partners = allPartners.slice();
  const mainPartner = partners.length && partners[0].pricePerNight && partners[0].priceTotal ? partners[0] : null;
  let numPartners = partners.length;
  let mainPartnerCancellationPolicy = null;
  if (mainPartner) {
    partners.shift();
    numPartners = partners.length;
    if (hasCancellationPolicy) {
      const mainPartnerFirstCancellationPolicy = Object.keys(mainPartner.cancellationPolicy)[0];
      mainPartnerCancellationPolicy = {
        days: mainPartner.cancellationPolicy[mainPartnerFirstCancellationPolicy],
        percentage: mainPartnerFirstCancellationPolicy,
      };
    }
  }

  const pluralizePartners = numPartners > 1;
  const partnersToShow = mainPartner ? 3 : 4;
  const showMaxGuests = maxGuests > 0 && maxGuests < 99;
  const showRooms = rooms > 0 && !(rooms > searchGuests);

  return (
    <div className={ `page-establishment-info ${className}` }>
      <div className="page-establishment-info__header-container">
        { breadcrumb ? <Breadcrumb breadcrumbInfo={ breadcrumb } /> : null }
        <div className="page-establishment-info__header-container__title">
          <h1>
            <div className="page-establishment-info__header-container__title__name">
              { <Title area={ area } categoryLiteral={ categoryLiteral } city={ city } t={ t } text={ title } titlePreposition={ titlePreposition } /> }
              <span className="page-establishment-info__header-container__title__name__stars-container">
                { stars ? [...Array(stars || 0)].map(i => <span key={ i } className="page-establishment-info__header-container__title__name__stars-container__star hundredicons-star-filled" />) : '' }
              </span>
            </div>
          </h1>
        </div>
        <div className="page-establishment-info__header-container__amenities">
          { area
            ? (
              <span className="page-establishment-info__header-container__amenities__amenitie">
                { `${area}m²` }
              </span>
            ) : null
          }
          { (area && showMaxGuests)
            ? <span className="page-establishment-info__header-container__amenities__separator" />
            : null
          }
          { showMaxGuests
            ? (
              <span className="page-establishment-info__header-container__amenities__amenitie">
                { `${maxGuests} ${maxGuests > 1 ? t('Guests') : t('Guest')}` }
              </span>
            ) : null
          }
          { (area || showMaxGuests) && showRooms
            ? <span className="page-establishment-info__header-container__amenities__separator" />
            : null
          }
          { showRooms
            ? (
              <span className="page-establishment-info__header-container__amenities__amenitie">
                { `${rooms} ${rooms > 1 ? t('Rooms') : t('Room')}` }
              </span>
            ) : null
          }
          { (showRooms || showMaxGuests || area) && city
            ? <span className="page-establishment-info__header-container__amenities__separator" />
            : null
          }
          { city
            ? (
              <span className="page-establishment-info__header-container__amenities__amenitie">
                { city }
              </span>
            ) : null
          }
        </div>
        <div className="page-establishment-info__header-container__properties">
          <div className="page-establishment-info__header-container__properties__icons">
            { !recommendation ? null
              : (
                <Tooltip
                  baseClassName="page-establishment-info__tooltip"
                  position="bottom"
                  space={ 0 }
                  title={ t('Recommendation of accommodations visited by users with interests similar to yours.') }
                  tooltipX={ 30 }
                >
                  <div className="page-establishment-info__header-container__properties__icons__container">
                    <span className="page-establishment-info__header-container__properties__icons__icon hundredicons-recomended-icon" />
                  </div>
                </Tooltip>
              )
            }
            { mainPartner && mainPartner.cancellationPolicy && Object.keys(mainPartner.cancellationPolicy).length !== 0 && hasCancellationPolicy && hasCancellationPolicy.has100PercentCancellationPolicy
              && (
                <Tooltip
                  baseClassName="page-establishment-info__tooltip"
                  position="bottom"
                  space={ 0 }
                  title={
                    (mainPartner.cancellationPolicy['100'] === 0)
                      ? t("Holiday rental with free cancellation until the day of your arrival.")
                      : t("Holiday rental with free cancellation up to {cancellationPolicyDays} days before your arrival.", { cancellationPolicyDays: mainPartner.cancellationPolicy['100'] })
                  }
                  tooltipX={ 50 }
                >
                  <div
                    className="page-establishment-info__header-container__properties__icons__container"
                    onClick={ handleScroll.bind(this, '.establishment-page__content__right-side__cancellation') }
                  >
                    <div className="page-establishment-info__header-container__properties__cancellation_policy">
                      <span className="hundredicons-cancellation-simple -full">
                        { t('Free cancellation') }
                      </span>
                    </div>
                  </div>
                </Tooltip>
              )
            }
            { mainPartner && mainPartner.cancellationPolicy && Object.keys(mainPartner.cancellationPolicy).length !== 0 && hasCancellationPolicy && !hasCancellationPolicy.has100PercentCancellationPolicy
              && (
                <Tooltip
                  baseClassName="page-establishment-info__tooltip"
                  position="bottom"
                  space={ 0 }
                  title={
                    (mainPartnerCancellationPolicy.days === 0)
                      ? t("This holiday rental is partially refundable up to the check-in date.")
                      : t("This holiday rental is partially refundable up to {cancellationPolicyDays} days before the check-in date.", { cancellationPolicyDays: mainPartnerCancellationPolicy.days })
                  }
                  tooltipX={ 50 }
                >
                  <div
                    className="page-establishment-info__header-container__properties__icons__container"
                    onClick={ handleScroll.bind(this, '.establishment-page__content__right-side__cancellation') }
                  >
                    <div className="page-establishment-info__header-container__properties__cancellation_policy">
                      <span className="hundredicons-cancellation-simple -partial ">
                        { t('Partial Cancellation') }
                      </span>
                    </div>
                  </div>
                </Tooltip>
              )
            }
          </div>
        </div>
      </div>
      <div className="page-establishment-info__body-container">
        { combinedRating > 5
          ? (
            <div>
              <div className="page-establishment-info__body-container__separation-line" />
              <div className="page-establishment-info__body-container__average-container">
                <div className="page-establishment-info__body-container__average-container__left-average" onClick={ handleScroll.bind(this, '#establishment-page-hundred-rating') }>
                  { t("Rating") }
                </div>
                <div className="page-establishment-info__body-container__average-container__right-average">
                  <Tooltip
                    baseClassName="page-establishment-info__tooltip__rating"
                    position="top"
                    space={ 0 }
                    title={ t("Hundred Rating evaluates authentic guest experiences alongside existing ratings from other websites.") }
                    tooltipX={ 100 }
                  >
                    <span onClick={ handleScroll.bind(this, '#establishment-page-hundred-rating') }>
                      { combinedRating > 5 && combinedRating < 10 ? combinedRating.toFixed(1) : 10 }
                    </span>
                  </Tooltip>
                </div>
              </div>
            </div>
          ) : null
        }
        <div className="page-establishment-info__body-container__separation-line" />
        { numberOfComments > 0
          && (
            <div>
              <div className="page-establishment-info__body-container__valoration-container" onClick={ partners.length && partners.length > 1 ? handleScroll.bind(this, '#establishment-page-grid-matching') : null }>
                <div className="page-establishment-info__body-container__valoration-container__left-valoration">
                  { t('Opinions') }
                </div>
                <div
                  className="page-establishment-info__body-container__valoration-container__right-valoration"
                >
                  <span className="page-establishment-info__body-container__valoration-container__right-valoration -no-cursor">
                    { `${numberOfComments} ${t('comments')}` }
                  </span>
                </div>
              </div>
              <div className="page-establishment-info__body-container__separation-line" />
            </div>
          )
        }
        <div className="page-establishment-info__body-container__dates-container">
          <div className="page-establishment-info__body-container__dates-container__left-dates">
            { t("dates") }
          </div>
          { (arrival || leaving) && numberOfNights > 0
            ? (
              <div className="page-establishment-info__body-container__dates-container__right-dates">
                { arrival }
                <img alt="Right arrow" className="establishment-modal__box-best-partner__dates__icon" src="/img/search/arrow-right.svg" />
                { leaving }
              </div>
            )
            : (
              <div className="page-establishment-info__body-container__dates-container__right-dates">
                { t("Not Available") }
              </div>
            )
          }
        </div>
        <div className="page-establishment-info__body-container__separation-line" />
        { mainPartner
          && (
            <div className="page-establishment-info__body-container__price-container">
              <div className="page-establishment-info__body-container__price-container__left-price">
                <div className="page-establishment-info__body-container__price-container__left-price__top">
                  { t("Best price") }
                </div>
                <div className="page-establishment-info__body-container__price-container__left-price__bottom">
                  <span className="page-establishment-info__body-container__price-container__left-price__bottom__partner_name">
                    { mainPartner.visibleName }
                  </span>
                  { mainPartner && mainPartner.cancellationPolicy && Object.keys(mainPartner.cancellationPolicy).length !== 0 && hasCancellationPolicy && hasCancellationPolicy.has100PercentCancellationPolicy
                && (
                  <Tooltip
                    baseClassName="page-establishment-info__tooltip"
                    position="bottom"
                    space={ 0 }
                    title={
                      (mainPartner.cancellationPolicy['100'] === 0)
                        ? t("Holiday rental with free cancellation until the day of your arrival.")
                        : t("Holiday rental with free cancellation up to {cancellationPolicyDays} days before your arrival.", { cancellationPolicyDays: mainPartner.cancellationPolicy['100'] })
                    }
                    tooltipX={ 50 }
                  >
                    <span className="hundredicons-cancellation-simple -full" />
                  </Tooltip>
                )
                  }
                  { mainPartner && mainPartner.cancellationPolicy && Object.keys(mainPartner.cancellationPolicy).length !== 0 && hasCancellationPolicy && !hasCancellationPolicy.has100PercentCancellationPolicy
                && (
                  <Tooltip
                    baseClassName="page-establishment-info__tooltip"
                    position="bottom"
                    space={ 0 }
                    title={
                      (mainPartnerCancellationPolicy.days === 0)
                        ? t("This holiday rental is partially refundable up to the check-in date.")
                        : t("This holiday rental is partially refundable up to {cancellationPolicyDays} days before the check-in date.", { cancellationPolicyDays: mainPartnerCancellationPolicy.days })
                    }
                    tooltipX={ 50 }
                  >
                    <span className="hundredicons-cancellation-simple -partial" />
                  </Tooltip>
                )
                  }
                </div>
              </div>
              <div className="page-establishment-info__body-container__price-container__right-price">
                <div className="page-establishment-info__body-container__price-container__right-price__top">
                  { maxDayPrice && minDayPrice && maxDayPrice !== minDayPrice
                    ? (
                      <Tooltip
                        key="page-establishment-info__body-container__price-container__right-price__top__discount__tooltip"
                        baseClassName="page-establishment-info__body-container__price-container__right-price__top__discount__tooltip"
                        position="left"
                        space={ 0 }
                        title={ t("Highest price for this holiday rental when compared with other websites") }
                        tooltipX={ 50 }
                      >
                        <div className="page-establishment-info__body-container__price-container__right-price__top__discount">
                          { t("{price}£", { price: maxDayPrice }) }
                        </div>
                      </Tooltip>
                    ) : null
                  }
                  <div className="page-establishment-info__body-container__price-container__right-price__top__price">
                    { t("{price}£", { price: numberFormat(mainPartner.pricePerNight) }) }
                  </div>
                  <span className="page-establishment-info__body-container__price-container__right-price__top__night">
                    { t("/night") }
                  </span>
                </div>
                <div className="page-establishment-info__body-container__price-container__right-price__bottom">
                  { numberOfNights === 1 ? t("{mtp}£ for 1 night", { mtp: numberFormat(mainPartner.priceTotal) }) : t("{mtp}£ for {non} nights", { mtp: numberFormat(mainPartner.priceTotal), non: numberOfNights }) }
                </div>
              </div>
            </div>
          )
        }
        { numPartners === 0
          ? (
            <div className="page-establishment-info__body-container__partners-container">
              <div className="page-establishment-info__body-container__partners-container__no-partners">
                { t("This is the only offer we found for this establishment") }
              </div>
            </div>
          )
          : (
            <div className="page-establishment-info__body-container__partners-container">
              { partners[0].pricePerNight
              && (
                <div className="page-establishment-info__body-container__partners-container__left-partners">
                  { t("prices in others webs") }
                </div>
              )
              }
              { mainPartner && !partners[0].pricePerNight
              && (
                <div className="page-establishment-info__body-container__partners-container__left-partners">
                  { pluralizePartners ? t("{options} more options", { options: numPartners <= partnersToShow ? numPartners : partnersToShow }) : t("1 more option") }
                </div>
              )
              }
              { !mainPartner && !partners[0].pricePerNight
              && (
                <div className="page-establishment-info__body-container__partners-container__left-partners">
                  { pluralizePartners ? t("{options} options", { options: numPartners <= partnersToShow ? numPartners : partnersToShow }) : t("1 option") }
                </div>
              )
              }
              <div className="page-establishment-info__body-container__partners-container__right-partners">
                { partners.map((partner, i) => (i < partnersToShow
                  ? (
                    <div key={ i } className="page-establishment-info__body-container__partners-container__right-partners__partner">
                      { partner.instantlyBooking
                        && (
                          <Tooltip
                            baseClassName="page-establishment-info__tooltip"
                            position="top"
                            space={ 0 }
                            title={ t("Book your holiday rental without waiting for the host's approval") }
                            tooltipX={ 80 }
                          >
                            <span className="page-establishment-info__body-container__partners-container__right-partners__partner__isInstant hundredicons-bolt-filled" />
                          </Tooltip>
                        )
                      }
                      <span className="page-establishment-info__body-container__partners-container__right-partners__partner__name">
                        { partner.visibleName }
                      </span>
                      { !(!partner.pricePerNight && !partner.priceTotal && partnersWithoutAvailabilityClickout.includes(partner.id))
                      && (
                        <span
                          className="page-establishment-info__body-container__partners-container__right-partners__partner__price"
                          onClick={ partner.handleClickout(clickoutSubsections.ESTABLISHMENT_MATCHING) }
                        >
                          { partner.pricePerNight ? t("{price}£", { price: numberFormat(partner.pricePerNight) }) : t('Check') }
                        </span>
                      ) }
                    </div>
                  ) : ''
                ))
                }
              </div>
            </div>
          )
        }
        { (mainPartner && mainPartner.pricePerNight && !global.isPhone)
          ? (
            <div className="page-establishment-info__body-container__button-container">
              <Button
                className="page-establishment-info__body-container__button-container__button"
                handleOnClick={ mainPartner.pricePerNight !== null && mainPartner.priceTotal !== null ? mainPartner.handleClickout(clickoutSubsections.ESTABLISHMENT_CTA) : '' }
              >
                { !instantBook ? null
                  : (
                    <Tooltip
                      baseClassName="page-establishment-info__tooltip"
                      position="top"
                      space={ 0 }
                      title={ t("Book your holiday rental without waiting for the host's approval") }
                      tooltipX={ 75 }
                    >
                      <span className="page-establishment-info__body-container__button-container__button__icon hundredicons-bolt-filled" />
                    </Tooltip>
                  )
                }
                { t("See offer in {name}", { name: mainPartner.visibleName }) }
              </Button>
              <div className="page-establishment-info__body-container__button-container__bottom">
                { t("We are going to send you to {name} so you can make the purchase", { name: mainPartner.visibleName }) }
              </div>
            </div>
          ) : ''
        }
      </div>
    </div>
  );
}

PageEstablishmentInfo.defaultProps = {
  area: 0,
  breadcrumb: null,
  categoryLiteral: '',
  className: '',
  clickoutSubsections: {},
  combinedRating: 0,
  maxDayPrice: null,
  maxGuests: 2,
  minDayPrice: null,
  numberFormat: n => n,
  numberOfComments: 0,
  numberOfNights: 1,
  partnersWithoutAvailabilityClickout: [],
  rooms: 1,
  searchGuests: 2,
  stars: 0,
  title: '',
  titlePreposition: '',
};

PageEstablishmentInfo.propTypes = {
  allPartners: PropTypes.arrayOf(PropTypes.shape({
    clickoutLink: PropTypes.string,
    favicon: PropTypes.string,
    id: PropTypes.number,
    instantlyBooking: PropTypes.bool,
    pricePerNight: PropTypes.number,
    priceTotal: PropTypes.number,
    visibleName: PropTypes.string,
  })).isRequired,
  area: PropTypes.number,
  arrival: PropTypes.string.isRequired,
  breadcrumb: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string,
    url: PropTypes.string,
  })),
  categoryLiteral: PropTypes.string,
  city: PropTypes.string.isRequired,
  className: PropTypes.string,
  clickoutSubsections: PropTypes.shape(),
  combinedRating: PropTypes.number,
  handleScroll: PropTypes.func.isRequired,
  hasCancellationPolicy: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]).isRequired,
  instantBook: PropTypes.bool.isRequired,
  leaving: PropTypes.string.isRequired,
  maxDayPrice: PropTypes.number,
  maxGuests: PropTypes.number,
  minDayPrice: PropTypes.number,
  numberFormat: PropTypes.func,
  numberOfComments: PropTypes.number,
  numberOfNights: PropTypes.number,
  partnersWithoutAvailabilityClickout: PropTypes.arrayOf(PropTypes.number),
  recommendation: PropTypes.bool.isRequired,
  rooms: PropTypes.number,
  searchGuests: PropTypes.number,
  stars: PropTypes.number,
  t: PropTypes.func.isRequired,
  title: PropTypes.string,
  titlePreposition: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};
